package com.karyadina.service

import com.karyadina.model.*
import kotlinx.coroutines.*
import com.karyadina.util.currentTimeMillis

/**
 * Service for managing study sessions and schedules
 */
interface StudyPlannerService {
    /**
     * Create a study session
     */
    fun createSession(session: StudySession): StudySession
    
    /**
     * Update study session
     */
    fun updateSession(session: StudySession): StudySession
    
    /**
     * Delete session
     */
    fun deleteSession(sessionId: String)
    
    /**
     * Get session by ID
     */
    fun getSession(sessionId: String): StudySession?
    
    /**
     * Get all sessions
     */
    fun getAllSessions(): List<StudySession>
    
    /**
     * Get sessions for a specific date range
     */
    fun getSessionsInRange(startTime: Long, endTime: Long): List<StudySession>
    
    /**
     * Get today's sessions
     */
    fun getTodaySessions(): List<StudySession>
    
    /**
     * Get this week's sessions
     */
    fun getWeekSessions(): List<StudySession>
    
    /**
     * Mark session as completed
     */
    fun completeSession(sessionId: String): StudySession
    
    /**
     * Create/update a subject
     */
    fun saveSubject(subject: Subject): Subject
    
    /**
     * Get all subjects
     */
    fun getAllSubjects(): List<Subject>
    
    /**
     * Delete subject
     */
    fun deleteSubject(subjectId: String)
    
    /**
     * Get statistics
     */
    fun getStatistics(): StudyStats
    
    /**
     * Get study progress for a subject
     */
    fun getSubjectProgress(subjectId: String): Float

    /** Re-read all data from persistent storage (e.g. after cloud sync import) */
    fun refreshFromStorage()
}

/**
 * Implementation of StudyPlannerService
 */
class StudyPlannerServiceImpl(
    private val storage: StudyPlannerStorage = StudyPlannerStorage()
) : StudyPlannerService {
    private val sessions = mutableListOf<StudySession>()
    private val subjects = mutableListOf<Subject>()
    
    init {
        // Load data from storage on initialization
        sessions.addAll(storage.loadSessions())
        subjects.addAll(storage.loadSubjects())
        
        // Listen for storage refresh signals
        kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.Dispatchers.Default).launch {
            AppStorage.refreshNotifier.collect {
                refreshFromStorage()
            }
        }
    }

    override fun refreshFromStorage() {
        sessions.clear()
        subjects.clear()
        sessions.addAll(storage.loadSessions())
        subjects.addAll(storage.loadSubjects())
    }
    
    override fun createSession(session: StudySession): StudySession {
        val now = currentTimeMillis()
        val newSession = session.copy(
            id = generateId(),
            createdAt = now,
            updatedAt = now
        )
        sessions.add(0, newSession)
        storage.saveSessions(sessions)
        return newSession
    }
    
    override fun updateSession(session: StudySession): StudySession {
        val index = sessions.indexOfFirst { it.id == session.id }
        if (index != -1) {
            val updated = session.copy(updatedAt = currentTimeMillis())
            sessions[index] = updated
            storage.saveSessions(sessions)
            return updated
        }
        throw IllegalArgumentException("Session not found: ${session.id}")
    }
    
    override fun deleteSession(sessionId: String) {
        sessions.removeAll { it.id == sessionId }
        storage.saveSessions(sessions)
    }
    
    override fun getSession(sessionId: String): StudySession? {
        return sessions.find { it.id == sessionId }
    }
    
    override fun getAllSessions(): List<StudySession> {
        return sessions.sortedByDescending { it.startTime }
    }
    
    override fun getSessionsInRange(startTime: Long, endTime: Long): List<StudySession> {
        return sessions.filter { 
            it.startTime >= startTime && it.startTime < endTime 
        }.sortedBy { it.startTime }
    }
    
    override fun getTodaySessions(): List<StudySession> {
        val now = currentTimeMillis()
        val todayStart = now - (now % (24 * 60 * 60 * 1000))
        val todayEnd = todayStart + (24 * 60 * 60 * 1000)
        
        // Return all incomplete sessions + today's completed sessions
        return sessions.filter { session ->
            !session.completed || // All incomplete sessions
            (session.completed && session.startTime >= todayStart && session.startTime < todayEnd) // Today's completed
        }.sortedBy { it.startTime }
    }
    
    override fun getWeekSessions(): List<StudySession> {
        val now = currentTimeMillis()
        val weekStart = now - (now % (7 * 24 * 60 * 60 * 1000))
        val weekEnd = weekStart + (7 * 24 * 60 * 60 * 1000)
        return getSessionsInRange(weekStart, weekEnd)
    }
    
    override fun completeSession(sessionId: String): StudySession {
        val session = getSession(sessionId) ?: throw IllegalArgumentException("Session not found: $sessionId")
        val completedSession = session.copy(completed = true)
        return updateSession(completedSession)
    }
    
    override fun saveSubject(subject: Subject): Subject {
        val existing = subjects.indexOfFirst { it.id == subject.id }
        if (existing != -1) {
            val updated = subject.copy(updatedAt = currentTimeMillis())
            subjects[existing] = updated
        } else {
            val now = currentTimeMillis()
            val newSubject = subject.copy(
                id = if (subject.id.isBlank()) generateId() else subject.id,
                createdAt = now,
                updatedAt = now
            )
            subjects.add(newSubject)
            storage.saveSubjects(subjects)
            return newSubject
        }
        storage.saveSubjects(subjects)
        return subjects.find { it.id == subject.id } ?: subject
    }
    
    override fun getAllSubjects(): List<Subject> {
        return subjects.sortedBy { it.name }
    }
    
    override fun deleteSubject(subjectId: String) {
        subjects.removeAll { it.id == subjectId }
        storage.saveSubjects(subjects)
    }
    
    override fun getStatistics(): StudyStats {
        val completedSessions = sessions.filter { it.completed }
        // Use actualDurationMinutes instead of durationMinutes for actual time spent
        val totalHours = completedSessions.sumOf { it.actualDurationMinutes } / 60f
        
        val now = currentTimeMillis()
        val todayStart = now - (now % (24 * 60 * 60 * 1000))
        val weekStart = todayStart - (6 * 24 * 60 * 60 * 1000)
        val monthStart = now - (30 * 24 * 60 * 60 * 1000)
        
        val todayHours = completedSessions
            .filter { it.startTime >= todayStart }
            .sumOf { it.actualDurationMinutes } / 60f
        
        val weekHours = completedSessions
            .filter { it.startTime >= weekStart }
            .sumOf { it.actualDurationMinutes } / 60f
        
        val monthHours = completedSessions
            .filter { it.startTime >= monthStart }
            .sumOf { it.actualDurationMinutes } / 60f
        
        val avgDaily = if (completedSessions.isNotEmpty()) monthHours / 30f else 0f
        
        val subjectHours = completedSessions.groupBy { it.subject }
            .mapValues { it.value.sumOf { s -> s.actualDurationMinutes } / 60f }
        val mostStudied = subjectHours.maxByOrNull { it.value }?.key
        
        // Calculate streak
        val sortedSessions = completedSessions.sortedByDescending { it.startTime }
        var currentStreak = 0
        var lastDay: Long? = null
        
        for (session in sortedSessions) {
            val sessionDay = session.startTime / (24 * 60 * 60 * 1000)
            if (lastDay == null || sessionDay == lastDay - 1) {
                currentStreak++
                lastDay = sessionDay
            } else if (sessionDay < lastDay!! - 1) {
                break
            }
        }
        
        return StudyStats(
            totalHours = totalHours,
            todayHours = todayHours,
            weekHours = weekHours,
            monthHours = monthHours,
            averageDailyHours = avgDaily,
            mostStudiedSubject = mostStudied,
            currentStreak = currentStreak,
            longestStreak = currentStreak // Simplified
        )
    }
    
    override fun getSubjectProgress(subjectId: String): Float {
        val subject = subjects.find { it.id == subjectId } ?: return 0f
        val weekStart = currentTimeMillis() - (7 * 24 * 60 * 60 * 1000)
        
        // Use actualDurationMinutes for actual time spent
        val weekHours = sessions
            .filter { it.subject == subject.name && it.startTime >= weekStart && it.completed }
            .sumOf { it.actualDurationMinutes } / 60f
        
        return if (subject.targetHoursPerWeek > 0) {
            (weekHours / subject.targetHoursPerWeek * 100).coerceAtMost(100f)
        } else 0f
    }
    
    private fun generateId(): String {
        return "id_${currentTimeMillis()}_${(1000..9999).random()}"
    }
}
